import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * The three states every screen has now that there is a server behind it: waiting,
 * failed, and genuinely empty.
 *
 * ONE MODULE, BECAUSE THE ALTERNATIVE WAS TWELVE. Eleven screens each grew a
 * loading branch and a retry the day the fixtures went, and eleven hand-written
 * versions of "Couldn't load that" is how one screen ends up apologising, one
 * explaining, and one spinning forever. Consistency across screens is a rule
 * (CLAUDE.md); it is cheapest to keep by there being one of each.
 *
 * NO SPINNER, ANYWHERE. A spinner says only that something is happening, and it
 * says it in the middle of an empty screen — so the first thing anybody sees of this
 * app is a widget. Blocks in the shape of the content that is coming say the same
 * thing and say what is coming, and when the content lands nothing jumps.
 *
 * THE COPY LABELS AND DOES NOT EXPLAIN (docs/1 § Copy). An error is one clause and a
 * button; it never guesses at a cause, never blames a network, and never reassures.
 * An empty state is one clause and, where there is genuinely something to do, one
 * control — never a paragraph teaching the product to somebody who has just arrived.
 */

/** The empty-state measure, matched by every screen that had one before there was a
 *  server: centred, balanced, muted, and in the space the content would occupy. */
const NOTE = 'px-8 py-16 text-center text-[14px] text-balance text-muted-foreground';

/**
 * One clause where the content would be, with an optional way to act.
 *
 * `children` is a LINE, not a paragraph. If what you are about to pass needs a full
 * stop in the middle of it, it is teaching rather than labelling and belongs in the
 * documentation instead.
 */
export function Note({
  children,
  action,
  className,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  if (!action) return <p className={cn(NOTE, className)}>{children}</p>;

  return (
    <div className={cn('flex flex-col items-center gap-3', NOTE, className)}>
      <p>{children}</p>
      {/* Wraps, because two pills and a long room name do not fit a narrow phone
          on one line. */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {action}
      </div>
    </div>
  );
}

/**
 * What is coming, in blocks.
 *
 * TWO SHAPES ONLY: `sheets` for the feed and a timeline, where the content is cards
 * a gap apart, and `rows` for the loft, the people lists and the notifications,
 * where it is a list. A third shape would be a screen with a bespoke skeleton, which
 * is where this stops being worth sharing.
 */
export function Loading({
  kind = 'sheets',
  count = 3,
}: {
  kind?: 'sheets' | 'rows';
  count?: number;
}) {
  return (
    /* `aria-hidden` plus one live region: a screen reader should hear "Loading"
       once, not the geometry of six empty boxes. */
    <div className="pt-3">
      <span className="sr-only" role="status">
        Loading
      </span>
      <div
        aria-hidden
        className={cn(
          'flex animate-pulse flex-col',
          kind === 'sheets' ? 'gap-3 px-4' : 'gap-1 px-3',
        )}
      >
        {Array.from({ length: count }, (_, i) => (
          <div
            key={i}
            className={cn(
              'rounded-xl bg-muted',
              kind === 'sheets' ? 'h-28' : 'h-16 rounded-2xl',
            )}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * It did not load.
 *
 * A RETRY AND NOTHING ELSE. No status code, no cause, no "check your connection" —
 * the only useful thing anybody can do about a failed read is ask again, and the
 * button is that. `message` exists for the one case where the server said something
 * a person should read (`ApiError.message`); the default is used everywhere else.
 */
export function Failed({
  onRetry,
  message = 'That didn’t load.',
}: {
  onRetry: () => void;
  message?: string;
}) {
  return (
    <Note
      action={
        <Button variant="outline" className="rounded-full" onClick={onRetry}>
          Try again
        </Button>
      }
    >
      {message}
    </Note>
  );
}
